// Textos de interfaz del modulo de Guias, en un solo lugar y por clave.
//
// Antes vivian como `isEnglish ? "Buy guide" : "Comprar guía"` repartidos en
// tres pantallas: 37 ternarios. Funcionaba, pero nada obligaba a escribir los
// dos idiomas —un literal suelto se veia igual en ingles y en espanol y nadie se
// enteraba— ni habia forma de revisar de un vistazo que estuviera todo.
//
// Aqui cada texto es una clave con sus DOS idiomas juntos, de modo que olvidar
// uno es visible al escribirlo, y `guides2Texts.test.js` lo convierte en una
// prueba que falla.
//
// Por que no el esquema remoto (`/api/lang/sch`, las claves LB/AL): el modulo de
// guias no tiene ninguna clave publicada ahi —comprobado: cero usos de `tOr` o
// `texts[...]` en las cuatro pantallas—, y las claves las da de alta backend, no
// el front. El dia que existan, este archivo es el respaldo: se consulta el
// esquema primero y se cae aqui, igual que hace `eventCategories`.
//
// Los valores pueden ser una funcion cuando el texto lleva un dato dentro.
export const GUIDES2_TEXTS = {
  // Encabezado y filtros
  pageTitle: { es: "Guías", en: "Guides" },
  filterAll: { es: "Todas las guías", en: "All guides" },
  filterPurchased: { es: "Mis guías compradas", en: "My purchased guides" },
  // Pestana del perfil, junto a "Mis eventos". El titulo va aqui y no en el
  // esquema remoto por el mismo motivo que el resto del modulo: Guias todavia no
  // tiene claves LB publicadas.
  // En ingles va en mayusculas como "My Events", la pestana de al lado.
  myGuidesTab: { es: "Mis guías", en: "My Guides" },

  // Sello de estado sobre la portada
  badgePurchased: { es: "Comprada", en: "Purchased" },
  badgePaid: { es: "De pago", en: "Paid" },
  badgeFree: { es: "Gratuita", en: "Free" },

  // Modalidades disponibles, en el pie de la tarjeta. Comparten palabra con los
  // sellos pero no significan lo mismo: aqui dicen que versiones existen.
  variantFree: { es: "Gratuita", en: "Free" },
  variantFull: { es: "Completa", en: "Full" },
  // En la liga de contratacion, donde solo se vende la version de pago.
  variantPaid: { es: "De pago", en: "Paid" },

  // Acciones de la tarjeta
  readFree: { es: "Leer versión gratuita", en: "Read free version" },
  readFull: { es: "Leer versión completa", en: "Read full version" },
  purchaseRequired: { es: "Requiere compra", en: "Purchase required" },
  seePrice: { es: "Ver precio", en: "See price" },
  hidePrice: { es: "Ocultar precio", en: "Hide price" },

  // Panel de compra
  priceLabel: { es: "Precio", en: "Price" },
  chargedIn: {
    es: (currency) => `Se cobra en ${currency}.`,
    en: (currency) => `Charged in ${currency}.`,
  },
  // Cuando la guía NO está en la moneda del cobro. Dice dos hechos y ninguna
  // promesa: en qué moneda está publicado el precio, y en cuál está definido el
  // cobro. El "todavía no está resuelta" es deliberado — el front no tiene tipo
  // de cambio y no debe inventarlo, y quien lea la pantalla merece saber que ahí
  // falta una decisión en vez de leer un importe que parece final.
  currencyMismatchNote: {
    es: (publicada, cobro) =>
      `Precio publicado en ${publicada}. El cobro de guías está definido en ${cobro} y esa conversión todavía no está resuelta.`,
    en: (publicada, cobro) =>
      `Price published in ${publicada}. Guides are charged in ${cobro}, and that conversion is not resolved yet.`,
  },
  buyGuide: { es: "Comprar guía", en: "Buy guide" },
  // Desglose del precio (VIVA-1254). El total se muestra como "Total a pagar"
  // en español y "Total" en inglés: no es un capricho de estilo, es que en
  // español la palabra sola se confunde con el precio base de la fila de arriba.
  priceBase: { es: "Precio base", en: "Base price" },
  betaDiscountRow: { es: "Descuento Beta", en: "Beta discount" },
  couponRow: { es: "Cupón", en: "Coupon" },
  totalRow: { es: "Total a pagar", en: "Total" },
  floorNote: {
    es: "El cobro mínimo es de 4.99 USD.",
    en: "The minimum charge is 4.99 USD.",
  },

  // Captura del cupón
  couponQuestion: { es: "¿Tienes un cupón?", en: "Have a coupon?" },
  couponPlaceholder: { es: "Código del cupón", en: "Coupon code" },
  couponApply: { es: "Aplicar", en: "Apply" },
  couponEmpty: {
    es: "Escribe el código antes de aplicarlo.",
    en: "Enter the code before applying it.",
  },
  couponApplied: {
    es: (code) => `Cupón ${code} aplicado.`,
    en: (code) => `Coupon ${code} applied.`,
  },
  couponRemove: { es: "Quitar cupón", en: "Remove coupon" },
  // Motivos de rechazo que devuelve precio-preview en `couponReasonCode`. Solo
  // NOT_FOUND está comprobado en pre (22/09/2026); cualquier otro motivo cae
  // en `couponRejected`, que no afirma nada que no sepamos.
  couponNotFound: {
    es: "Ese cupón no existe. Revisa el código.",
    en: "That coupon does not exist. Check the code.",
  },
  couponRejected: {
    es: "Ese cupón no se puede aplicar a esta guía.",
    en: "That coupon can't be applied to this guide.",
  },
  couponCheckError: {
    es: "No fue posible validar el cupón. Puedes intentarlo de nuevo o comprar sin él.",
    en: "The coupon could not be validated. You can try again or buy without it.",
  },

  // Cobro (VIVA-1214). El total que se muestra es el que calcula el servidor en
  // precio-preview, con el piso de 4.99 USD ya aplicado.
  previewLoading: { es: "Calculando el total…", en: "Calculating total…" },
  loadingGuides: { es: "Cargando guías…", en: "Loading guides…" },
  previewError: {
    es: "No fue posible calcular el precio de esta guía. Inténtalo más tarde.",
    en: "The price of this guide could not be calculated. Please try again later.",
  },
  // Paso previo al pago de una guía Beta (VIVA-1165).
  betaConfirmTitle: { es: "Antes de comprar", en: "Before you buy" },
  betaConfirmContinue: { es: "Continuar al pago", en: "Continue to payment" },
  paymentTitle: { es: "Completar pago", en: "Complete payment" },
  paymentAmount: {
    es: (amount) => `Vas a pagar ${amount}.`,
    en: (amount) => `You are paying ${amount}.`,
  },
  payNow: { es: "Pagar ahora", en: "Pay now" },
  cancel: { es: "Cancelar", en: "Cancel" },
  paymentStartError: {
    es: "No fue posible iniciar el pago. Inténtalo de nuevo en unos momentos.",
    en: "The payment could not be started. Please try again in a moment.",
  },
  priceChanged: {
    es: "El precio de la guía cambió. Revisa el nuevo total antes de pagar.",
    en: "The guide price changed. Review the new total before paying.",
  },
  paymentFormError: {
    es: "No se pudo cargar el formulario de pago.",
    en: "The payment form could not be loaded.",
  },
  paymentConfirmError: {
    es: "No se pudo confirmar el pago. Revisa los datos e inténtalo de nuevo.",
    en: "The payment could not be confirmed. Check your details and try again.",
  },
  paymentUnavailable: {
    es: "El pago en línea no está disponible en este momento.",
    en: "Online payment is not available right now.",
  },
  paymentProcessing: {
    es: "Tu pago se está procesando. La guía aparecerá en «Mis guías compradas» en cuanto se confirme.",
    en: "Your payment is processing. The guide will appear in “My purchased guides” once it is confirmed.",
  },
  paymentSuccess: {
    es: "¡Pago completado! Ya puedes leer la guía.",
    en: "Payment completed! You can now read the guide.",
  },
  // Retorno de la autenticación 3DS del banco (25/09/2026). El resultado no lo
  // decide la URL con la que Stripe devuelve al comprador, sino el servidor; el
  // aviso de fallo no afirma nada sobre el cargo porque esta pantalla no lo
  // sabe, solo que el pago no se completó.
  paymentRedirectChecking: {
    es: "Confirmando tu pago con el banco…",
    en: "Confirming your payment with the bank…",
  },
  paymentRedirectFailed: {
    es: "El pago no se completó. Puedes intentarlo de nuevo.",
    en: "The payment was not completed. You can try again.",
  },
  paymentPendingAccess: {
    es: "Pago recibido. La guía se habilitará en unos momentos en «Mis guías compradas».",
    en: "Payment received. The guide will be enabled shortly in “My purchased guides”.",
  },
  waitingAccess: {
    es: "Pago recibido. Habilitando tu guía…",
    en: "Payment received. Enabling your guide…",
  },
  // VIVA-1499 (parche temporal): lo que se dice justo antes de recargar.
  refreshingAfterPayment: {
    es: "Actualizando la página para mostrar tu compra…",
    en: "Refreshing the page to show your purchase…",
  },

  // Datos de la tarjeta
  authorPrefix: { es: "Por", en: "By" },
  purchasedOn: { es: "Comprada el", en: "Purchased on" },
  // No es un texto, es el formato de fecha del idioma. Vive aqui porque se
  // olvidaba igual que un texto.
  dateLocale: { es: "es-MX", en: "en-US" },

  // Errores y estados vacios
  errorAuth: {
    es: "Inicia sesión para ver el catálogo de guías.",
    en: "Sign in to browse the guides catalog.",
  },
  errorCatalog: {
    es: "No fue posible cargar las guías. Inténtalo de nuevo.",
    en: "The guides could not be loaded. Please try again.",
  },
  emptySaleTitle: {
    es: "No hay guías a la venta en esta página",
    en: "No guides for sale on this page",
  },
  emptySaleText: {
    es: "Las guías de esta página son solo gratuitas. Continúa a la siguiente.",
    en: "The guides on this page are free only. Continue to the next page.",
  },
  emptyPurchasedTitle: {
    es: "Sin guías compradas",
    en: "No purchased guides",
  },
  emptyPurchasedText: {
    es: "Todavía no has comprado ninguna guía.",
    en: "You haven't purchased any guides yet.",
  },
  emptyCatalogTitle: {
    es: "No hay guías disponibles",
    en: "No guides available",
  },
  emptyCatalogText: {
    es: "Todavía no hay guías publicadas.",
    en: "There are no published guides yet.",
  },

  // Paginacion
  paginationLabel: { es: "Páginas del catálogo", en: "Catalog pages" },
  previous: { es: "Anterior", en: "Previous" },
  next: { es: "Siguiente", en: "Next" },
  page: { es: "Página", en: "Page" },
  // Solo donde se conoce el total: "Pagina 2 de 5", igual que el listado de
  // eventos. El catalogo no sabe cuantas paginas hay y ahi no se usa.
  pageOf: { es: "de", en: "of" },

  // Liga oculta de contratacion
  signInTitle: {
    es: "Inicia sesión para continuar",
    en: "Sign in to continue",
  },
  signInText: {
    es: "Las guías quedan ligadas a tu cuenta de Viva Nómada, así que comprar una necesita que entres. Al autenticarte vuelves a esta misma pantalla.",
    en: "Guides are tied to your Viva Nómada account, so buying one needs you to sign in. You will come back to this page afterwards.",
  },
  signIn: { es: "Iniciar sesión", en: "Sign in" },
  createAccount: { es: "Crear cuenta", en: "Create account" },

  // Visor de la guia
  viewerUnknownVariant: {
    es: "Versión de la guía desconocida.",
    en: "Unknown guide version.",
  },
  viewerNoAccess: {
    es: "No tienes acceso a esta versión de la guía.",
    en: "You don't have access to this version of the guide.",
  },
  viewerNoContent: {
    es: "La guía no tiene contenido para mostrar.",
    en: "The guide has no content to display.",
  },
  // El detalle tecnico del fallo ya queda en consola desde el repository, el
  // service y el propio visor. Aqui va lo unico que le sirve a quien lee: que no
  // cargo y que puede reintentar. La version anterior le pedia al usuario
  // "verificar que su sesion tenga acceso a este endpoint", que es una
  // instruccion de depuracion, no algo que pueda hacer.
  viewerLoadError: {
    es: "No fue posible cargar la guía. Vuelve a intentarlo en unos momentos.",
    en: "The guide could not be loaded. Please try again in a moment.",
  },
  viewerBack: { es: "Volver", en: "Back" },

  // Hilo de comentarios de la guia (VIVA-1166). Solo van aqui los textos PROPIOS
  // del modulo: el resto de la seccion —boton, contador, avisos, confirmacion de
  // borrado— ya viene en los dos idiomas dentro del componente reutilizable
  // (`components/comments/commentsTexts.js`), y repetirlo aqui seria mantener dos
  // diccionarios para la misma pantalla.
  feedbackTitle: {
    es: "Comentarios sobre la guía",
    en: "Comments about this guide",
  },
  feedbackPlaceholder: {
    es: "Cuéntanos qué te pareció esta guía...",
    en: "Tell us what you thought about this guide...",
  },
  feedbackEmptyTitle: {
    es: "Abre la conversación",
    en: "Start the conversation",
  },
  feedbackEmptyText: {
    es: "Todavía nadie ha comentado esta guía. Lo que cuentes le sirve a quien la lea después.",
    en: "Nobody has commented on this guide yet. What you share helps whoever reads it next.",
  },
};

// Devuelve el traductor del idioma activo: `const t = createGuidesText(isEnglish)`.
//
// Una clave inexistente no tumba la pantalla —se veria una tarjeta rota en
// produccion por un texto— pero deja rastro en consola, y la prueba que recorre
// el codigo fuente no deja que llegue hasta ahi.
export const createGuidesText = (isEnglish) => (key, ...args) => {
  const entry = GUIDES2_TEXTS[key];

  if (!entry) {
    console.error(`Texto de guías sin definir: ${key}`);
    return "";
  }

  const value = isEnglish ? entry.en : entry.es;
  return typeof value === "function" ? value(...args) : value;
};
